// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Live readings per asset, indexed by what they measure.
 *
 * The twin publishes a measurement as a *telemetry point entity* hanging off
 * its asset (`SWGR-1-A hasTelemetryPoint <ActivePower point>`), with the value
 * in the point's `state` under a key that names the quantity. Some payloads
 * also put values straight on the asset's own `state`. Both are read here into
 * one `asset id → quantity kind → reading` index, built once per poll, so the
 * live pass can ask "what is this board's active power, and how old is it?"
 * without rescanning the relationship array.
 *
 * Every reading keeps its timestamp. Freshness is a first-class property of a
 * value in the live pass — a stale zero must never energize or de-energize a
 * conductor — and the index is where the timestamp is preserved for that.
 */

import type { Entity, Relationship, ValueEnvelope } from '../types/digitalTwin';
import { buildTelemetryChildIndex } from './entityHelpers';
import { parseQuantity, type Quantity } from './quantity';

export interface Reading extends Quantity {
  /** Epoch ms of the value, or NaN when the point carried no timestamp. */
  at: number;
}

/** Asset id → quantity kind → reading. */
export type ReadingIndex = Map<string, Map<string, Reading>>;

/** State keys that are not measurements. */
const NON_MEASUREMENT_KEYS = new Set(['updatedAt', 'status']);

const parseTime = (value: unknown): number =>
  typeof value === 'string' ? new Date(value).getTime() : NaN;

const readEnvelope = (
  key: string,
  raw: unknown,
  fallbackAt: number,
  into: Map<string, Reading>
): void => {
  if (NON_MEASUREMENT_KEYS.has(key)) return;
  if (!raw || typeof raw !== 'object') return;
  const envelope = raw as ValueEnvelope;
  const quantity = parseQuantity(envelope);
  if (!quantity) return;
  const at = parseTime(envelope.timestamp);
  into.set(key, { ...quantity, at: Number.isNaN(at) ? fallbackAt : at });
};

/**
 * Build the index.
 *
 * A point's own state key is the quantity kind (`ActivePower`, `SoC`,
 * `Run_Status`). Where a point publishes several keys, all are kept. The
 * asset's own state is read first and a child point overrides it for the same
 * kind — a point is the more specific source.
 */
export const indexReadings = (
  entities: readonly Entity[],
  relationships: readonly Relationship[]
): ReadingIndex => {
  const entitiesById = new Map(entities.map(entity => [entity.id, entity]));
  const childIndex = buildTelemetryChildIndex(relationships as Relationship[]);
  const index: ReadingIndex = new Map();

  const readInto = (entity: Entity, into: Map<string, Reading>) => {
    const state = entity.state;
    if (!state) return;
    const fallbackAt = parseTime(state.updatedAt);
    for (const [key, raw] of Object.entries(state)) readEnvelope(key, raw, fallbackAt, into);
  };

  for (const entity of entities) {
    const readings = new Map<string, Reading>();
    readInto(entity, readings);
    for (const childId of childIndex.get(entity.id) ?? []) {
      const child = entitiesById.get(childId);
      if (child) readInto(child, readings);
    }
    if (readings.size > 0) index.set(entity.id, readings);
  }

  return index;
};

/** The first reading present among `kinds`, in order of preference. */
export const readAny = (
  index: ReadingIndex,
  assetId: string,
  kinds: readonly string[]
): Reading | undefined => {
  const readings = index.get(assetId);
  if (!readings) return undefined;
  for (const kind of kinds) {
    const reading = readings.get(kind);
    if (reading) return reading;
  }
  return undefined;
};

/**
 * The first *fresh* reading among `kinds`, in preference order.
 *
 * `readAny` picks the first kind that exists and only then asks whether it is
 * fresh, so a stale preferred phase hides a fresh equivalent behind it. Rules
 * that need current evidence use this; the stale-zero standby exception keeps
 * `readAny`, because there staleness is the point.
 */
export const readFresh = (
  index: ReadingIndex,
  assetId: string,
  kinds: readonly string[],
  now: number,
  staleAfterMs: number
): Reading | undefined => {
  const readings = index.get(assetId);
  if (!readings) return undefined;
  for (const kind of kinds) {
    const reading = readings.get(kind);
    if (reading && isFresh(reading, now, staleAfterMs)) return reading;
  }
  return undefined;
};

/** Every reading present among `kinds`. */
export const readAll = (
  index: ReadingIndex,
  assetId: string,
  kinds: readonly string[]
): Reading[] => {
  const readings = index.get(assetId);
  if (!readings) return [];
  return kinds.map(kind => readings.get(kind)).filter((r): r is Reading => r !== undefined);
};

/**
 * True when the reading is recent enough to drive an inference.
 *
 * Recent means within `staleAfterMs` *behind* now. A timestamp ahead of now is
 * a clock the twin and the browser disagree about, and it drives nothing.
 */
export const isFresh = (reading: Reading, now: number, staleAfterMs: number): boolean => {
  // No timestamp: nothing to judge against. We do not invent staleness.
  if (Number.isNaN(reading.at)) return true;
  const age = now - reading.at;
  return age >= 0 && age <= staleAfterMs;
};

// --- Quantity kinds, by role ------------------------------------------------
// Grouped by what a rule needs rather than by device, and ordered by
// preference. `Voltage` last: it is the generic name a point falls back to.

export const INPUT_VOLTAGE_KINDS = [
  'InputVoltageL1L2',
  'InputVoltageL2L3',
  'InputVoltageL3L1',
  'InputVoltageL1N',
  'InputVoltage',
  'Voltage',
] as const;

export const INPUT_VOLTAGE_LN_KINDS = [
  'InputVoltageL1N',
  'InputVoltageL2N',
  'InputVoltageL3N',
] as const;

export const OUTPUT_VOLTAGE_KINDS = [
  'OutputVoltageL1L2',
  'OutputVoltageL2L3',
  'OutputVoltageL3L1',
  'OutputVoltageL1N',
  'OutputVoltage',
] as const;

/** What an asset draws. */
export const LOAD_POWER_KINDS = [
  'ActivePower',
  'ImportActivePower',
  'InputActivePower',
  'Electric_Power_Sensor',
] as const;

/** What an asset puts out — a genset's export, a UPS's output. */
export const OUTPUT_POWER_KINDS = ['ExportActivePower', 'OutputActivePower'] as const;

export const APPARENT_POWER_KINDS = ['ApparentPower'] as const;

export const INPUT_PHASE_CURRENT_KINDS = [
  'InputCurrentL1',
  'InputCurrentL2',
  'InputCurrentL3',
] as const;
export const OUTPUT_PHASE_CURRENT_KINDS = [
  'OutputCurrentL1',
  'OutputCurrentL2',
  'OutputCurrentL3',
] as const;

export const RUN_STATUS_KINDS = ['Run_Status', 'RunStatus'] as const;
export const SOC_KINDS = ['SoC', 'StateOfCharge'] as const;
export const FUEL_KINDS = ['FuelLevel'] as const;
export const FREQUENCY_KINDS = ['Frequency'] as const;
export const POWER_FACTOR_KINDS = ['PowerFactor'] as const;
